import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import PatientDashboardShell from './PatientDashboardShell.tsx';
import AnimatedRoutes from '../../global/component/organism/AnimatedRoute/AnimatedRoute.tsx';
import PatientOverview from './Page/PatientOverview.tsx';
import PatientMedicalOverview from './Page/PatientMedicalOverview.tsx';
import PatientMedicalInformation from './Page/PatientMedicalInformation.tsx';
import LifeEventsForm from '../HcpPatientDashboard/Form/LifeEventForm.tsx';
import WrappedPhysiologicalDataTable from '../HcpPatientDashboard/Table/PhysiologicalDataTable.tsx';
import PhysiologicalForm from '../HcpPatientDashboard/Form/PhysiologicalForm.tsx';
import QuestionnaireForm from './form/QuestionnaireForm.tsx';
import TdmForm from './form/TdmForm.tsx';
import PatientProfileForm from '../PatientProfile/PatientProfileForm.tsx';
import ChangePassword from '../Login/ChangePassword/ChangePassword.tsx';
import WrappedEditPrescriptionTable from '../HcpPatientDashboard/Table/EditPrescriptionTable.tsx';
import TitleButton from '../../global/component/molecules/TitleButton.tsx';
import { TaskType } from '../../global/types';
import CollaboratorForm from './form/CollaboratorForm.tsx';
import TabbedCollaboratorView from './table/TabbedCollaboratorView.tsx';
import WrappeTherapeuticDrugMonitoringTable from '../HcpPatientDashboard/Widget/TherapeuticDrugMonitoring.tsx';
import DocumentsForm from '../HcpPatientDashboard/Form/DocumentForm.tsx';
import WrappedLifeEventsTable from '../HcpPatientDashboard/Table/LifeEventsTable.tsx';
import ResourcesPage from './Page/ResourcesPage';
import PatientAlerts from './Page/PatientAlerts.tsx';
import NoSeatsAvailable from './NoSeatsAvailable.tsx';
import { PatientJournalList } from './PatientJournal/PatientJournalList.tsx';
import { JournalEntryForm } from './PatientJournal/JournalEntryForm.tsx';
import CounsellingAcknowledgementPage from './Page/CounsellingAcknowledgementPage.tsx';
import { useTranslation } from 'react-i18next';
import EICFConsentPage from './eicf/EICFConsentPage.tsx';
import EICFConsentViewPage from './eicf/EICFConsentViewPage.tsx';

const PatientDashboardRoute: React.FC = () => {
    const { t } = useTranslation();
    return (
        <Routes>
            <Route path="/" element={<PatientDashboardShell />}>
                <Route index element={<Navigate to="mainPatient" replace />} />
                <Route path="profile" element={<PatientProfileForm />} />
                <Route path="initialProfile/:guid?" element={<PatientProfileForm />} />
                <Route path="changePassword" element={<ChangePassword />} />
                <Route path="no-seats" element={<NoSeatsAvailable />} />
                <Route
                    path="questionnaire/:id"
                    element={
                        <TitleButton taskType={TaskType.questionnaire} urlFallback={'/patientDashboard/mainPatient'} showGuidelinesHelpIcon={false}>
                            <QuestionnaireForm />
                        </TitleButton>
                    }
                />    
                <Route element={<AnimatedRoutes />}>
                    <Route index element={<PatientOverview />} />
                    <Route path="mainPatient" element={<PatientOverview />} />
                    <Route
                        path="task/:id"
                        element={
                            <TitleButton taskType={TaskType.tdm} urlFallback={'/patientDashboard/mainPatient'} showGuidelinesHelpIcon={false}>
                                <TdmForm />
                            </TitleButton>
                        }
                    />
                    <Route path="patientMedicalOverview" element={
                        <TitleButton label={t('navbar.patientMedicalOverview')} showGuidelinesHelpIcon={false}>
                            <PatientMedicalOverview />
                        </TitleButton>
                    } />
                    <Route path="patientMedicalOverview/physiologicalData" element={<PatientMedicalOverview />} />
                    <Route path="patientMedicalOverview/lifeEvents" element={<PatientMedicalOverview />} />
                    <Route path="patientMedicalInformation" element={
                        <TitleButton label={t('navbar.patientMedicalInformation')}>
                            <PatientMedicalInformation />
                        </TitleButton>
                    } />
                    <Route
                        path="patientMedicalInformation/documents/:id"
                        element={
                            <TitleButton
                                label={t('navbar.patientMedicalInformation')}
                                urlFallback={'/patientDashboard/patientMedicalInformation'}
                                showGuidelinesHelpIcon={false}
                            >
                                <DocumentsForm />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="patientMedicalInformation/editPrescription/:id"
                        element={
                            <TitleButton
                                label={t('navbar.patientMedicalInformation')}
                                urlFallback={'/patientDashboard/patientMedicalInformation'}
                                showGuidelinesHelpIcon={false}
                            >
                                <WrappedEditPrescriptionTable />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="biomarkers"
                        element={
                            <TitleButton label={t('navbar.biomarkers')} subLabel={t('biomarkers.biomarkersSubLabel')}>
                                <WrappeTherapeuticDrugMonitoringTable />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="journals"
                        element={
                            <TitleButton label={t('navbar.journals')} showGuidelinesHelpIcon={false}>
                                <PatientJournalList />  
                            </TitleButton>
                        }
                    />
                    <Route
                        path="journals/:journalId/new-entry/:entryDate"
                        element={
                            <TitleButton label={t('navbar.journals')} urlFallback={'/patientDashboard/journals'} showGuidelinesHelpIcon={false}>
                                <JournalEntryForm />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="journals/:journalId/entry/:entryId"
                        element={
                            <TitleButton label={t('navbar.journals')} urlFallback={'/patientDashboard/journals'} showGuidelinesHelpIcon={false}>
                                <JournalEntryForm />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="lifeEvents"
                        element={
                            <TitleButton label={t('navbar.lifeEvents')}>
                                <WrappedLifeEventsTable />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="lifeEvents/:id"
                        element={
                            <TitleButton label={t('navbar.lifeEvents')} urlFallback={'/patientDashboard/lifeEvents'} showGuidelinesHelpIcon={false}>
                                <LifeEventsForm />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="physiologicalData"
                        element={
                            <TitleButton label={t('navbar.physiologicalData')}>
                                <WrappedPhysiologicalDataTable />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="physiologicalData/:id"
                        element={
                            <TitleButton
                                label={t('navbar.physiologicalData')}
                                urlFallback={'/patientDashboard/physiologicalData'}
                                showGuidelinesHelpIcon={false}
                            >
                                <PhysiologicalForm />
                            </TitleButton>
                        }
                    />{' '}
                    <Route
                        path="collaborator"
                        element={
                            <TitleButton label={t('navbar.collaborator')} showGuidelinesHelpIcon={false}>
                                <TabbedCollaboratorView />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="collaborator/:id"
                        element={
                            <TitleButton
                                label={t('navbar.collaborator')}
                                urlFallback={'/patientDashboard/collaborator'}
                            >
                                <CollaboratorForm />
                            </TitleButton>
                        }
                    />
                    <Route
                        path="counsellingAcknowledgement/:id"
                        element={
                            <TitleButton
                                label={t('counsellingNote.patient.pageTitle')}
                                urlFallback={'/patientDashboard/mainPatient'}
                                showGuidelinesHelpIcon={false}
                            >
                                <CounsellingAcknowledgementPage />
                            </TitleButton>
                        }
                    />
                    <Route path="resources" element={<ResourcesPage />} />
                    <Route path="patientAlerts" element={<PatientAlerts />} />
                    <Route path="eicfConsent" element={<EICFConsentPage />} />
                    <Route
                        path="eicfConsentView"
                        element={
                            <TitleButton
                                label={t('navbar.myConsent')}
                                urlFallback={'/patientDashboard/mainPatient'}
                                showGuidelinesHelpIcon={false}
                            >
                                <EICFConsentViewPage />
                            </TitleButton>
                        }
                    />
                </Route>
                <Route path="*" element={<Navigate to="/mainPatient" />} />
            </Route>
        </Routes>
    );
};

export default PatientDashboardRoute;
